import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Upload, Info, Plus } from "lucide-react";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
  "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO",
  "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR",
  "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI",
  "WY", "DC",
];

const SPECIALTIES = [
  "ADHD", "Anger Management", "Anxiety", "Behavioral Issues", "Bipolar Disorder",
  "Burnout", "Career Counseling", "Child & Adolescent", "Chronic Illness",
  "College & Graduate Students", "Couples Counseling", "Depression", "Divorce",
  "Domestic Violence", "Eating Disorders", "Family Conflict", "Family Therapy",
  "Grief & Loss", "Infertility", "Life Transitions", "Military & Veterans",
  "Mood Disorders", "OCD", "Parenting", "Perinatal & Postpartum",
  "Personality Disorders", "PTSD & Trauma", "Racial Identity", "Relationship Issues",
  "Self-Esteem", "Sexual Abuse", "Sleep Disorders", "Social Anxiety", "Spirituality",
  "Stress Management", "Substance Use", "Suicidal Ideation", "Teen Issues",
  "Women's Issues", "Work-Life Balance",
];

const INSURANCE = [
  "Aetna", "Ambetter", "Anthem", "Blue Cross Blue Shield", "Carelon", "Cigna",
  "Florida Blue", "Humana", "Kaiser Permanente", "Medicaid", "Medicare",
  "Molina Healthcare", "Optum", "Oscar Health", "TRICARE", "UnitedHealthcare",
  "VA Community Care", "Out-of-Network", "Private Pay", "Sliding Scale",
];

const LANGUAGES = [
  "English", "Spanish", "French", "Portuguese", "Haitian Creole", "Arabic",
  "Mandarin Chinese", "Cantonese", "Korean", "Japanese", "Vietnamese", "Tagalog",
  "Hindi", "Urdu", "Punjabi", "Russian", "Ukrainian", "German", "Italian",
  "American Sign Language (ASL)",
];

const EMPTY = {
  photo: "", name: "", credentials: "", bio: "", modality: "telehealth",
  states: [], specialties: [], insurance: [], languages: ["English"],
  booking_url: "", email: "", phone: "", faith_informed: false, trauma_informed: false,
};

const SectionLabel = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.18em] text-[#291B2D]/50 font-semibold mb-3">{children}</p>
);

const inputCls =
  "w-full bg-white border border-[#291B2D]/12 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#C89B55] text-sm text-[#291B2D] placeholder:text-[#291B2D]/35";

function Chips({ options, selected, onToggle, testid, allowCustom, onAddCustom }) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    onAddCustom(v);
    setDraft("");
  };
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              aria-pressed={on}
              data-testid={`${testid}-${opt}`}
              className={`px-3.5 py-2 rounded-full text-sm border transition-smooth ${
                on
                  ? "bg-[#291B2D] text-white border-[#291B2D]"
                  : "bg-white text-[#291B2D]/75 border-[#291B2D]/12 hover:border-[#291B2D]/30"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {allowCustom && (
        <div className="flex items-center gap-2 mt-3">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
            placeholder="Add your own…"
            data-testid={`${testid}-custom-input`}
            className="flex-1 max-w-xs bg-white border border-[#291B2D]/12 rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#C89B55] text-[#291B2D] placeholder:text-[#291B2D]/35"
          />
          <button
            type="button"
            onClick={commit}
            data-testid={`${testid}-custom-add`}
            className="inline-flex items-center gap-1 rounded-full bg-[#F7F2EA] text-[#291B2D] px-4 py-2 text-sm font-medium hover:brightness-95 transition-smooth"
          >
            <Plus size={15} /> Add
          </button>
        </div>
      )}
    </div>
  );
}

function RecoverLink() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    setSending(true);
    try {
      await api.post("/therapists/edit-link", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <p className="text-center text-sm text-[#291B2D]/70" data-testid="recover-sent">
        If that email is in our directory, we've sent your private edit link. Please check your inbox.
      </p>
    );
  }

  if (!open) {
    return (
      <p className="text-center">
        <button type="button" onClick={() => setOpen(true)} data-testid="recover-open" className="text-sm text-[#C89B55] hover:underline">
          Already applied? Get your edit link
        </button>
      </p>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl p-4 card-soft" data-testid="recover-form">
      <p className="text-xs uppercase tracking-[0.15em] text-[#291B2D]/50 font-semibold mb-2">Get your edit link</p>
      <p className="text-sm text-[#291B2D]/70 mb-3">Enter the email you applied with and we'll send your private link.</p>
      <div className="flex items-center gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
          placeholder="you@practice.com"
          data-testid="recover-email"
          className="flex-1 bg-white border border-[#291B2D]/12 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C89B55] text-[#291B2D] placeholder:text-[#291B2D]/35"
        />
        <button type="button" onClick={send} disabled={sending} data-testid="recover-send"
          className="inline-flex items-center gap-1 rounded-full bg-[#291B2D] text-white px-5 py-2.5 text-sm font-medium hover:brightness-110 disabled:opacity-50 transition-smooth">
          {sending ? <Loader2 size={15} className="animate-spin" /> : "Send link"}
        </button>
      </div>
    </div>
  );
}


export default function TherapistApply() {
  const [form, setForm] = useState({ ...EMPTY });
  const [custom, setCustom] = useState({ specialties: [], insurance: [], languages: [] });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [editUrl, setEditUrl] = useState("");
  const photoRef = useRef(null);

  const { token } = useParams();
  const isEdit = !!token;
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/therapists/edit/${token}`);
        const norm = (arr) => (Array.isArray(arr) ? arr : []);
        const next = {
          photo: data.photo || "", name: data.name || "", credentials: data.credentials || "",
          bio: data.bio || "", modality: data.modality || "telehealth",
          states: norm(data.states), specialties: norm(data.specialties),
          insurance: norm(data.insurance), languages: norm(data.languages),
          booking_url: data.booking_url || "", email: data.email || "", phone: data.phone || "",
          faith_informed: !!data.faith_informed, trauma_informed: !!data.trauma_informed,
        };
        setForm(next);
        // any saved values not in the base lists become selectable custom chips
        setCustom({
          specialties: next.specialties.filter((v) => !SPECIALTIES.includes(v)),
          insurance: next.insurance.filter((v) => !INSURANCE.includes(v)),
          languages: next.languages.filter((v) => !LANGUAGES.includes(v)),
        });
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
    }));

  const addCustom = (field, base) => (value) => {
    const v = value.trim();
    const known = [...base, ...custom[field]];
    const dupe = known.find((o) => o.toLowerCase() === v.toLowerCase());
    if (dupe) {
      if (!form[field].includes(dupe)) toggle(field, dupe);
      return;
    }
    setCustom((c) => ({ ...c, [field]: [...c[field], v] }));
    setForm((f) => ({ ...f, [field]: [...f[field], v] }));
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/therapists/apply/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo", data.photo);
      toast.success("Headshot uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Please enter your name."); return; }
    if (!form.email.trim()) { toast.error("Please enter your email."); return; }
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.post(`/therapists/edit/${token}`, form);
        toast.success("Your profile has been updated.");
      } else {
        const { data } = await api.post("/therapists/apply", form);
        setEditUrl(data.edit_url || "");
      }
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F2EA] flex items-center justify-center" data-testid="therapist-apply-page">
        <Loader2 size={28} className="animate-spin text-[#C89B55]" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F7F2EA] flex items-center justify-center px-4" data-testid="therapist-apply-page">
        <div className="bg-white rounded-3xl p-10 text-center card-soft max-w-md" data-testid="edit-link-invalid">
          <h1 className="font-heading text-2xl">This edit link isn't valid.</h1>
          <p className="text-sm text-[#291B2D]/70 mt-2">The link may be incorrect or expired. Request a fresh link below, or apply again.</p>
          <div className="mt-6"><RecoverLink /></div>
          <Link to="/therapists/apply" className="inline-block mt-6 text-sm text-[#C89B55] hover:underline">Apply to the directory →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F2EA] py-8 px-4" data-testid="therapist-apply-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Brand hero */}
        <div className="bg-[#291B2D] text-white rounded-3xl p-8 lg:p-10 card-soft">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">{isEdit ? "Update your profile" : "Join the directory"}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mt-3">{isEdit ? "Keep your details fresh." : "Tell women who you are."}</h1>
          <p className="text-white/70 text-sm leading-relaxed mt-4 max-w-lg">
            {isEdit
              ? "Update anything below and save — your changes go live right away, and our team is notified of the edit."
              : "You're invited to be listed in the Sisterhood+ therapist directory — culturally responsive care that welcomes women from all backgrounds, with deep experience supporting women of color. Share your details below; our team reviews every profile before it goes live."}
          </p>
        </div>

        {done ? (
          <div className="bg-white rounded-3xl p-10 text-center card-soft" data-testid="apply-success">
            <CheckCircle2 size={40} className="text-[#3D6B52] mx-auto" />
            <h2 className="font-heading text-2xl mt-3">{isEdit ? "Your profile is updated." : "Thank you for applying."}</h2>
            <p className="text-sm text-[#291B2D]/70 mt-2 max-w-md mx-auto">
              {isEdit
                ? "Your changes have been saved. You can return to this same link anytime to update your details again."
                : "Our team will review your profile and reach out by email — check your inbox for your private link to edit your details anytime. Once approved, you'll appear in the Sisterhood+ directory."}
            </p>
            {isEdit ? (
              <button onClick={() => setDone(false)} data-testid="edit-again" className="inline-block mt-6 text-sm text-[#C89B55] hover:underline">Make more changes</button>
            ) : (
              <>
                {editUrl && (
                  <div className="mt-6 mx-auto max-w-md text-left bg-[#F7F2EA] rounded-2xl p-4" data-testid="apply-edit-link">
                    <p className="text-xs uppercase tracking-[0.15em] text-[#291B2D]/50 font-semibold mb-2">Your private edit link</p>
                    <div className="flex items-center gap-2">
                      <input readOnly value={editUrl} className="flex-1 bg-white border border-[#291B2D]/12 rounded-full px-3 py-2 text-xs text-[#291B2D]/70" />
                      <button type="button" data-testid="copy-edit-link"
                        onClick={() => { navigator.clipboard?.writeText(editUrl); toast.success("Link copied."); }}
                        className="rounded-full bg-[#291B2D] text-white px-4 py-2 text-xs font-medium hover:brightness-110">Copy</button>
                    </div>
                    <p className="text-[11px] text-[#291B2D]/45 mt-2">Bookmark this to update your profile anytime. We've also emailed it to you.</p>
                  </div>
                )}
                <Link to="/therapists" className="inline-block mt-6 text-sm text-[#C89B55] hover:underline">View the directory →</Link>
              </>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft space-y-8">
            {/* Headshot */}
            <div>
              <SectionLabel>Headshot</SectionLabel>
              <p className="text-sm text-[#291B2D]/60 -mt-1 mb-3">A warm, real photo helps women feel at ease. Square images look best.</p>
              <div className="flex items-center gap-4">
                {form.photo ? (
                  <img src={mediaUrl(form.photo)} alt="Your headshot" className="w-20 h-20 rounded-full object-cover border border-[#291B2D]/10" data-testid="apply-photo-preview" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[#F7F2EA] flex items-center justify-center text-[#C89B55]"><Upload size={22} /></div>
                )}
                <input ref={photoRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="apply-photo-file" />
                <button type="button" onClick={() => photoRef.current?.click()} disabled={uploading} data-testid="apply-photo-upload"
                  className="inline-flex items-center gap-2 bg-[#291B2D] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:brightness-110 disabled:opacity-50 transition-smooth">
                  {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {form.photo ? "Replace headshot" : "Upload headshot"}
                </button>
                {form.photo && <button type="button" onClick={() => set("photo", "")} className="text-xs text-[#9B2C2C] hover:underline">Remove</button>}
              </div>
            </div>

            {/* Full name */}
            <div>
              <SectionLabel>Full name *</SectionLabel>
              <input className={inputCls} data-testid="apply-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Dr. Maya Bell" />
            </div>

            {/* Credentials */}
            <div>
              <SectionLabel>Credentials</SectionLabel>
              <input className={inputCls} data-testid="apply-credentials" value={form.credentials} onChange={(e) => set("credentials", e.target.value)} placeholder="LCSW, PhD" />
            </div>

            {/* About */}
            <div>
              <SectionLabel>About you</SectionLabel>
              <textarea rows={4} className={inputCls} data-testid="apply-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="A few warm sentences about your approach and who you love working with." />
            </div>

            {/* Modality */}
            <div>
              <SectionLabel>How you see clients</SectionLabel>
              <select className={inputCls} data-testid="apply-modality" value={form.modality} onChange={(e) => set("modality", e.target.value)}>
                <option value="telehealth">Telehealth</option>
                <option value="in-person">In-person</option>
                <option value="both">Both</option>
              </select>
            </div>

            {/* States */}
            <div>
              <SectionLabel>States you're licensed in</SectionLabel>
              <Chips options={US_STATES} selected={form.states} onToggle={(v) => toggle("states", v)} testid="apply-states" />
            </div>

            {/* Specialties */}
            <div>
              <SectionLabel>Specialties</SectionLabel>
              <Chips options={[...SPECIALTIES, ...custom.specialties]} selected={form.specialties} onToggle={(v) => toggle("specialties", v)} testid="apply-specialties" allowCustom onAddCustom={addCustom("specialties", SPECIALTIES)} />
            </div>

            {/* Insurance */}
            <div>
              <SectionLabel>Insurance accepted</SectionLabel>
              <Chips options={[...INSURANCE, ...custom.insurance]} selected={form.insurance} onToggle={(v) => toggle("insurance", v)} testid="apply-insurance" allowCustom onAddCustom={addCustom("insurance", INSURANCE)} />
            </div>

            {/* Languages */}
            <div>
              <SectionLabel>Languages</SectionLabel>
              <Chips options={[...LANGUAGES, ...custom.languages]} selected={form.languages} onToggle={(v) => toggle("languages", v)} testid="apply-languages" allowCustom onAddCustom={addCustom("languages", LANGUAGES)} />
            </div>

            {/* Booking */}
            <div>
              <SectionLabel>Booking link / website</SectionLabel>
              <input className={inputCls} data-testid="apply-booking" value={form.booking_url} onChange={(e) => set("booking_url", e.target.value)} placeholder="https://…" />
            </div>

            {/* Email + phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <SectionLabel>Email *</SectionLabel>
                <input className={inputCls} type="email" data-testid="apply-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@practice.com" />
              </div>
              <div>
                <SectionLabel>Phone</SectionLabel>
                <input className={inputCls} data-testid="apply-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" />
              </div>
            </div>

            {/* Care flags */}
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-[#291B2D]/80">
                <input type="checkbox" data-testid="apply-faith" checked={form.faith_informed} onChange={(e) => set("faith_informed", e.target.checked)} /> Faith-informed
              </label>
              <label className="flex items-center gap-2 text-sm text-[#291B2D]/80">
                <input type="checkbox" data-testid="apply-trauma" checked={form.trauma_informed} onChange={(e) => set("trauma_informed", e.target.checked)} /> Trauma-informed
              </label>
            </div>

            <div className="flex items-center gap-2 text-xs text-[#291B2D]/55">
              <Info size={14} className="text-[#C89B55] shrink-0" /> {isEdit ? "Changes are saved instantly; our team is notified of any updates." : "Your profile is reviewed by our team before it appears publicly."}
            </div>

            <button onClick={submit} disabled={submitting} data-testid="apply-submit"
              className="w-full bg-[#291B2D] text-white rounded-full py-4 font-medium hover:brightness-110 disabled:opacity-50 transition-smooth flex items-center justify-center gap-2">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> {isEdit ? "Saving…" : "Submitting…"}</> : (isEdit ? "Save changes" : "Submit for review")}
            </button>

            <p className="text-[11px] text-[#291B2D]/45 text-center">
              Sisterhood+ lists independent providers and is not a referral service or medical provider.
            </p>
          </div>
        )}

        {!isEdit && !done && <RecoverLink />}
        <p className="text-center"><Link to="/therapists" className="text-sm text-[#C89B55] hover:underline">View the directory</Link></p>
      </div>
    </div>
  );
}
